import React from 'react';
import { FaPhone, FaClock, FaShieldAlt, FaCheckCircle } from 'react-icons/fa';
import Seo from '../../components/Seo/Seo';
import BookingForm from '../../components/BookingForm/BookingForm';

const Booking = () => {
  const steps = [
    {
      number: '1',
      title: 'Choose Service',
      description: 'Select the cleaning service you need from our range of options.',
    },
    {
      number: '2',
      title: 'Pick Date & Time',
      description: 'Choose a convenient date and time that works for your schedule.',
    },
    {
      number: '3',
      title: 'Confirm Booking',
      description: 'Fill in your details and submit. We\'ll send you a confirmation email.',
    },
    {
      number: '4',
      title: 'We Clean',
      description: 'Our professional team arrives on time and delivers a spotless clean.',
    },
  ];

  const benefits = [
    'No hidden fees — transparent pricing',
    'Same-day service available',
    'Fully insured and trained professionals',
    'Eco-friendly cleaning products',
    '100% satisfaction guarantee',
    'Free re-clean if not satisfied',
  ];

  return (
    <div>
      <Seo
        title="Book a Service"
        description="Book pest control or cleaning services with The Skilled Group online in a few simple steps."
        canonicalPath="/booking"
      />
      {/* Page Header */}
      <div className="page-header">
        <div className="container-custom">
          <h1>Book a Cleaning Service</h1>
          <p>
            Schedule your professional cleaning service in just a few steps.
            Fast, easy, and reliable.
          </p>
        </div>
      </div>

      {/* Booking Form Section */}
      <section className="section-padding bg-white pt-8 sm:pt-16 lg:pt-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Form */}
            <div className="lg:col-span-2">
              <BookingForm />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Benefits Card */}
              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100">
                <h3 className="text-lg font-heading font-semibold text-gray-800 mb-4">
                  Why Book With Us
                </h3>
                <ul className="space-y-3">
                  {benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-3">
                      <FaCheckCircle className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact Card */}
              <div className="bg-primary-500 rounded-2xl p-6 md:p-8 text-white">
                <h3 className="text-lg font-heading font-semibold mb-4">
                  Need Help Booking?
                </h3>
                <p className="text-primary-100 text-sm mb-6">
                  Our friendly team is here to assist you with your booking or answer any questions.
                </p>

                <div className="space-y-4">
                  <a
                    href="tel:0466708090"
                    className="flex items-center gap-3 text-white hover:text-accent-400 transition-colors"
                  >
                    <div className="w-10 h-10 bg-primary-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaPhone />
                    </div>
                    <div>
                      <p className="text-xs text-primary-200">Call Us</p>
                      <p className="font-semibold">0466708090</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaClock />
                    </div>
                    <div>
                      <p className="text-xs text-primary-200">Working Hours</p>
                      <p className="font-semibold text-sm">Mon-Sat: 7AM-7PM</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaShieldAlt />
                    </div>
                    <div>
                      <p className="text-xs text-primary-200">Guarantee</p>
                      <p className="font-semibold text-sm">100% Satisfaction</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-heading font-bold text-gray-800 mb-4">
              How It Works
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Booking a cleaning service with The Skilled Group is simple and straightforward.
            </p>
          </div>

          {/* Mobile: compact dropdown steps */}
          <div className="sm:hidden space-y-3">
            {steps.map((step) => (
              <details
                key={step.number}
                className="bg-white border border-gray-200 rounded-xl px-4 py-3"
              >
                <summary className="list-none cursor-pointer flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-gray-800">
                    <span className="text-xs font-bold text-primary-500 mr-1">{step.number})</span>
                    {step.title}
                  </span>
                  <span className="text-gray-400 text-xs">View</span>
                </summary>
                <p className="text-gray-500 text-sm mt-3 leading-relaxed">
                  {step.description}
                </p>
              </details>
            ))}
          </div>

          {/* Tablet/Desktop: existing card layout */}
          <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={step.number} className="text-center relative">
                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-primary-100" />
                )}

                <div className="w-16 h-16 bg-primary-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold relative z-10">
                  {step.number}
                </div>
                <h3 className="text-lg font-heading font-semibold text-gray-800 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-500 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;